import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  cancelChefJob,
  observeChefJob,
  retryChefJob,
} from '../../services/aiService';
import {
  addMessage,
  Conversation,
  getConversation,
  getConversations,
  markConversationAsRead,
  Message,
} from '../../services/conversationService';
import {
  moveConversationSecretFromRecipe,
  saveConversationSecret,
} from '../../services/secretService';
import { useAppShell } from '../../contexts/AppShellContext';
import StreamingSaltShaker from '../StreamingSaltShaker';
import { PlanLimitCard } from '../Billing/PlanLimitPrompt';
import { BillingLimit, billingLimitFromError } from '../../utils/billingLimit';
import './ChatBox.css';

type Props = {
  recipeId?: number;
  recipeName?: string;
  onJobStateChange?: (jobId: string | null, active: boolean) => void;
  recipeGenerationActive?: boolean;
  onFirstSend?: (message: string) => Promise<void>;
  focusRequest?: number;
};

// Keep unsent messages for the lifetime of the current page without
// persisting them across reloads or mixing drafts between recipes.
const recipeDrafts = new Map<number, string>();

const LEGACY_RECIPE_GREETING = 'I’m Chef. How can I help you build and run this recipe?';
const STARTER_SUGGESTIONS = [
  'Review for problems',
  'Explain recipe execution schedule',
  'what is the Kitchen?',
  'what is a Recipe?',
  'Change how often this recipe runs',
];

const TypingDots = () => (
  <span className="typing-dots" aria-label="Chef is responding"><span /><span /><span /></span>
);

const MessageBody: React.FC<{ text: string; renderMarkup?: boolean }> = ({ text, renderMarkup = true }) => {
  if (!renderMarkup) {
    return <div className="whitespace-pre-wrap" dir="auto">{text}</div>;
  }

  return (
    <div className="chat-markdown" dir="auto">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, children, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer">{children}</a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

const MessageFooter: React.FC<{ message: Message }> = ({ message }) => {
  const [copied, setCopied] = useState(false);
  const timestamp = message.timestamp ? new Date(message.timestamp) : null;
  const validTimestamp = timestamp && !Number.isNaN(timestamp.getTime()) ? timestamp : null;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message.message);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mt-1 flex items-center gap-1.5 px-1 text-[10px] leading-none text-gray-400">
      {validTimestamp && (
        <time dateTime={validTimestamp.toISOString()} title={validTimestamp.toLocaleString()}>
          {validTimestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </time>
      )}
      <button
        type="button"
        onClick={() => void copyMessage()}
        className="rounded p-0.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={copied ? 'Message copied' : 'Copy message to clipboard'}
        title={copied ? 'Copied' : 'Copy message'}
      >
        {copied ? (
          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="m3.5 8.2 2.7 2.7 6.3-6.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <rect x="5.25" y="5.25" width="7.5" height="7.5" rx="1.5" />
            <path d="M10.75 5.25V4.5A1.25 1.25 0 0 0 9.5 3.25h-6A1.25 1.25 0 0 0 2.25 4.5v6A1.25 1.25 0 0 0 3.5 11.75h.75" strokeLinecap="round" />
          </svg>
        )}
      </button>
    </div>
  );
};

const SecretCard: React.FC<{
  conversationId: string;
  message: Message;
  onResolved: () => Promise<void>;
}> = ({ conversationId, message, onResolved }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const label = String(message.metadata?.secretLabel || 'private information');

  const save = async () => {
    if (!value.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      await saveConversationSecret(conversationId, message.id!, value);
      setValue('');
      await onResolved();
    } catch (reason: any) {
      setError(reason?.response?.data?.message || 'Could not save this securely.');
    } finally {
      setSaving(false);
    }
  };

  const move = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await moveConversationSecretFromRecipe(conversationId, message.id!);
      await onResolved();
      document.dispatchEvent(new CustomEvent('refreshRecipeContent'));
    } catch (reason: any) {
      setError(reason?.response?.data?.message || 'Could not move this securely.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-3 text-gray-800">
      <div className="text-sm font-semibold">Secure information needed</div>
      <div className="mt-1 text-sm">{message.message}</div>
      {message.resolvedAt ? (
        <div className="mt-2 text-xs font-medium text-emerald-700">Saved securely</div>
      ) : message.metadata?.secretSource === 'recipe' ? (
        <button onClick={move} disabled={saving} className="mt-3 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
          {saving ? 'Moving…' : 'Move it to secure storage'}
        </button>
      ) : (
        <div className="mt-3 flex gap-2">
          <input
            type="password"
            value={value}
            onChange={event => setValue(event.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') void save(); }}
            placeholder={`Paste ${label}`}
            autoComplete="new-password"
            className="min-w-0 flex-1 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
          />
          <button onClick={save} disabled={!value.trim() || saving} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
    </div>
  );
};

const emptyNewRecipeConversation = (): Conversation => ({
  id: '',
  title: 'New recipe',
  messages: [],
  recipeId: null,
  scope: 'recipe',
  origin: 'web',
  userId: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  isRead: true,
  chefJob: null,
});

const ChatBox: React.FC<Props> = ({ recipeId, recipeName, onJobStateChange, recipeGenerationActive = false, onFirstSend, focusRequest = 0 }) => {
  const [conversation, setConversation] = useState<Conversation | null>(() =>
    !recipeId && onFirstSend ? emptyNewRecipeConversation() : null
  );
  const [input, setInputState] = useState(() => recipeId ? recipeDrafts.get(recipeId) || '' : '');
  const [loading, setLoading] = useState(Boolean(recipeId));
  const [startingRecipe, setStartingRecipe] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [chefStatus, setChefStatus] = useState('');
  const [agentError, setAgentError] = useState('');
  const [planLimit, setPlanLimit] = useState<BillingLimit | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const anchoredMessageRef = useRef<HTMLDivElement>(null);
  const anchoredTurnEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const observedJobIdRef = useRef<string | null>(null);
  const stickToBottomRef = useRef(true);
  const previousScrollTopRef = useRef(0);
  const touchYRef = useRef<number | null>(null);
  const animateAnchorScrollRef = useRef(false);
  const [anchoredMessageId, setAnchoredMessageId] = useState<string | null>(null);
  const [anchoredTurnSpace, setAnchoredTurnSpace] = useState(0);
  const { skin } = useAppShell();
  const isYolo = skin === 'yolo';

  const scrollToBottom = useCallback((force = false) => {
    window.requestAnimationFrame(() => {
      if (!force && !stickToBottomRef.current) return;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      scroller.scrollTop = scroller.scrollHeight;
      previousScrollTopRef.current = scroller.scrollTop;
    });
  }, []);

  const setInput = useCallback((value: string) => {
    setInputState(value);
    if (!recipeId) return;
    if (value) {
      recipeDrafts.set(recipeId, value);
    } else {
      recipeDrafts.delete(recipeId);
    }
  }, [recipeId]);

  useEffect(() => {
    setInputState(recipeId ? recipeDrafts.get(recipeId) || '' : '');
  }, [recipeId]);

  const load = useCallback(async (quiet = false) => {
    if (!recipeId) {
      setLoading(false);
      return;
    }
    if (!quiet) setLoading(true);
    try {
      const threads = await getConversations(String(recipeId));
      const next = threads[0] || null;
      setConversation(current => {
        if (!current || !next) return next;
        return { ...next, messages: next.messages };
      });
      if (next && !next.isRead) void markConversationAsRead(next.id);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [recipeId]);

  useEffect(() => {
    stickToBottomRef.current = true;
    previousScrollTopRef.current = 0;
    setAnchoredMessageId(null);
    setAnchoredTurnSpace(0);
    setConversation(!recipeId && onFirstSend ? emptyNewRecipeConversation() : null);
    void load();
  }, [load, onFirstSend, recipeId]);

  useLayoutEffect(() => {
    if (!anchoredMessageId || !animateAnchorScrollRef.current) return;
    const scroller = scrollerRef.current;
    const message = anchoredMessageRef.current;
    if (!scroller || !message) return;
    const offset = message.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    const top = Math.max(0, scroller.scrollTop + offset - 16);
    scroller.scrollTo({ top, behavior: 'smooth' });
    animateAnchorScrollRef.current = false;
  }, [anchoredMessageId]);

  useLayoutEffect(() => {
    if (!anchoredMessageId) return;
    const scroller = scrollerRef.current;
    const message = anchoredMessageRef.current;
    const turnEnd = anchoredTurnEndRef.current;
    if (!scroller || !message || !turnEnd) return;
    const turnHeight = turnEnd.getBoundingClientRect().top - message.getBoundingClientRect().top;
    setAnchoredTurnSpace(Math.max(0, Math.ceil(scroller.clientHeight - turnHeight)));
  }, [anchoredMessageId, conversation?.messages.length, streaming, streamingText, chefStatus, agentError]);

  useEffect(() => {
    if (streaming) return;
    const poller = window.setInterval(() => {
      void load(true);
    }, 5000);
    return () => window.clearInterval(poller);
  }, [load, streaming]);

  useEffect(() => {
    const refresh = () => void load(true);
    document.addEventListener('refreshConversations', refresh);
    return () => document.removeEventListener('refreshConversations', refresh);
  }, [load]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation?.messages.length, scrollToBottom, streaming, streamingText]);

  useEffect(() => {
    if (conversation?.id) inputRef.current?.focus();
  }, [conversation?.id]);

  useEffect(() => {
    if (!focusRequest) return;
    const frame = window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input?.getClientRects().length) input.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusRequest]);

  useEffect(() => () => cleanupRef.current?.(), []);

  useEffect(() => {
    const job = conversation?.chefJob;
    const active = Boolean(job && (job.status === 'queued' || job.status === 'running'));
    onJobStateChange?.(job?.id || null, active);
  }, [conversation?.chefJob, onJobStateChange]);

  const observe = useCallback((jobId: string) => {
    if (observedJobIdRef.current === jobId) return;
    cleanupRef.current?.();
    observedJobIdRef.current = jobId;
    setStreaming(true);
    cleanupRef.current = observeChefJob(jobId, {
      onSnapshot: job => {
        setConversation(current => current ? { ...current, chefJob: job } : current);
        setChefStatus(job.statusText || 'Chef is working…');
        setStreamingText(current => current.length > (job.partialContent || '').length
          ? current
          : (job.partialContent || ''));
        setAgentError('');
      },
      onDelta: partialContent => {
        setStreamingText(partialContent);
        setChefStatus('Chef is responding…');
      },
      onComplete: async job => {
        observedJobIdRef.current = null;
        cleanupRef.current = null;
        setConversation(current => {
          if (!current) return current;

          const existingMessageIndex = job.finalMessageId
            ? current.messages.findIndex(message => message.id === job.finalMessageId)
            : -1;
          const completedMessage: Message | null = job.finalMessageId ? {
            id: job.finalMessageId,
            sender: 'ai',
            message: job.partialContent,
            timestamp: job.completedAt || new Date(),
            kind: 'chat',
          } : null;
          const messages = !completedMessage
            ? current.messages
            : existingMessageIndex >= 0
              ? current.messages.map((message, index) => index === existingMessageIndex
                ? { ...message, ...completedMessage }
                : message)
              : [...current.messages, completedMessage];

          return { ...current, chefJob: job, messages };
        });
        setStreaming(false);
        setStreamingText('');
        setChefStatus('');
        if (job.recipeMutated) {
          document.dispatchEvent(new CustomEvent('refreshRecipeContent', { detail: { recipeId } }));
          document.dispatchEvent(new CustomEvent('refreshChefMutation', { detail: { recipeId } }));
        }
        await load(true);
      },
      onCancelled: async () => {
        observedJobIdRef.current = null;
        cleanupRef.current = null;
        setStreaming(false);
        setStreamingText('');
        setChefStatus('');
        await load(true);
      },
      onError: async (error, job) => {
        observedJobIdRef.current = null;
        cleanupRef.current = null;
        setStreaming(false);
        setChefStatus('');
        if (job) {
          setConversation(current => current ? { ...current, chefJob: job } : current);
          setStreamingText(job.partialContent || '');
        } else {
          setAgentError(error.message);
        }
        await load(true);
      },
    });
  }, [load, recipeId]);

  useEffect(() => {
    const job = conversation?.chefJob;
    if (job && (job.status === 'queued' || job.status === 'running')) {
      observe(job.id);
    }
  }, [conversation?.chefJob, observe]);

  const loadOlder = async () => {
    if (!conversation?.nextCursor || !scrollerRef.current) return;
    const scroller = scrollerRef.current;
    const previousHeight = scroller.scrollHeight;
    const older = await getConversation(conversation.id, conversation.nextCursor);
    setConversation(current => current ? {
      ...current,
      messages: [...older.messages, ...current.messages],
      nextCursor: older.nextCursor,
    } : current);
    requestAnimationFrame(() => {
      scroller.scrollTop += scroller.scrollHeight - previousHeight;
      previousScrollTopRef.current = scroller.scrollTop;
    });
  };

  const send = async (suggestedMessage?: string) => {
    const text = (suggestedMessage ?? input).trim();
    if (!text || startingRecipe) return;
    if (!recipeId && onFirstSend) {
      setStartingRecipe(true);
      setAgentError('');
      setPlanLimit(null);
      try {
        await onFirstSend(text);
        setInput('');
      } catch (error: any) {
        const nextPlanLimit = billingLimitFromError(error);
        if (nextPlanLimit) setPlanLimit(nextPlanLimit);
        else setAgentError(error?.response?.data?.error || error?.response?.data?.message || error?.message || 'The message could not be sent.');
      } finally {
        setStartingRecipe(false);
      }
      return;
    }
    if (!conversation || ['queued', 'running'].includes(conversation.chefJob?.status || '')) return;
    setInput('');
    requestAnimationFrame(() => inputRef.current?.focus());
    setAgentError('');
    setPlanLimit(null);
    stickToBottomRef.current = false;
    const optimistic: Message = {
      id: `local-${Date.now()}`,
      sender: 'user',
      message: text,
      timestamp: new Date(),
      kind: 'chat',
    };
    animateAnchorScrollRef.current = true;
    setAnchoredMessageId(optimistic.id!);
    setAnchoredTurnSpace(scrollerRef.current?.clientHeight || 0);
    setConversation(current => current ? { ...current, messages: [...current.messages, optimistic] } : current);
    try {
      const saved = await addMessage(conversation.id, 'user', text);
      setConversation(saved);
      const job = saved.chefJob;
      if (!job) throw new Error('Chef did not accept the response job.');
      setAnchoredMessageId(job.userMessageId);
      setStreamingText(job.partialContent || '');
      setChefStatus(job.statusText || 'Chef is preparing the response…');
      observe(job.id);
    } catch (error: any) {
      setStreaming(false);
      const nextPlanLimit = billingLimitFromError(error);
      if (nextPlanLimit) {
        setPlanLimit(nextPlanLimit);
      } else {
        setAgentError(error?.response?.data?.error || error?.message || 'The message could not be sent.');
      }
      setAnchoredMessageId(null);
      setAnchoredTurnSpace(0);
      await load(true);
    }
  };

  const stopChef = async () => {
    const job = conversation?.chefJob;
    if (!job || !['queued', 'running'].includes(job.status)) return;
    const updated = await cancelChefJob(job.id);
    setConversation(current => current ? { ...current, chefJob: updated } : current);
    setChefStatus(updated.statusText || 'Stopping Chef…');
  };

  const retryChef = async () => {
    const job = conversation?.chefJob;
    if (!job || !['failed', 'cancelled'].includes(job.status)) return;
    setAgentError('');
    const updated = await retryChefJob(job.id);
    setConversation(current => current ? { ...current, chefJob: updated } : current);
    setStreamingText('');
    setChefStatus(updated.statusText || 'Chef is preparing the response…');
    observe(updated.id);
  };

  if (loading && !conversation) {
    return <div className="flex h-full items-center justify-center bg-white text-sm text-gray-500">Loading Chef…</div>;
  }
  if (!conversation) {
    return <div className="flex h-full items-center justify-center bg-white p-6 text-center text-sm text-gray-500">Chef is unavailable for this recipe.</div>;
  }

  const chefJob = conversation.chefJob;
  const chefActive = Boolean(chefJob && (chefJob.status === 'queued' || chefJob.status === 'running'));
  const chefTerminalProblem = chefJob && !chefJob.finalMessageId
    && (chefJob.status === 'failed' || chefJob.status === 'cancelled');
  const visibleMessages = conversation.messages.filter(message =>
    message.sender !== 'system'
    && !(isYolo && message.sender === 'ai' && message.message === LEGACY_RECIPE_GREETING)
  );
  const showWelcome = isYolo && visibleMessages.length === 0 && !streaming && !recipeGenerationActive;
  const showStarters = showWelcome;

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-white">
      <div
        ref={scrollerRef}
        onScroll={() => {
          const element = scrollerRef.current;
          if (!element) return;
          const scrolledUp = element.scrollTop < previousScrollTopRef.current - 1;
          const reachedBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 8;
          if (scrolledUp) {
            stickToBottomRef.current = false;
          } else if (reachedBottom) {
            stickToBottomRef.current = true;
          }
          previousScrollTopRef.current = element.scrollTop;
          if (element.scrollTop < 40 && conversation.nextCursor) void loadOlder();
        }}
        onWheel={event => {
          if (event.deltaY < 0) stickToBottomRef.current = false;
        }}
        onTouchStart={event => {
          touchYRef.current = event.touches[0]?.clientY ?? null;
        }}
        onTouchMove={event => {
          const nextY = event.touches[0]?.clientY ?? null;
          if (nextY !== null && touchYRef.current !== null && nextY > touchYRef.current) {
            stickToBottomRef.current = false;
          }
          touchYRef.current = nextY;
        }}
        onTouchEnd={() => {
          touchYRef.current = null;
        }}
        className={`min-h-0 flex-1 overflow-y-auto px-4 py-4 ${isYolo ? 'bg-[#fffdf8] pb-28' : ''}`}
      >
        {conversation.nextCursor && (
          <button onClick={() => void loadOlder()} className="mx-auto mb-4 block text-xs font-medium text-blue-600 hover:text-blue-800">
            Load earlier messages
          </button>
        )}
        {showWelcome && (
          <div className="mb-4 flex items-center gap-3 border-b border-[#f1e8d8] pb-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 to-orange-400 text-lg shadow-sm" aria-hidden="true">✦</div>
            <div>
              <div className="text-sm font-semibold text-stone-900">I’ve reviewed “{recipeName || 'this recipe'}.”</div>
              <div className="text-xs text-stone-500">I can help you understand it, check it for problems, or change how it runs.</div>
            </div>
          </div>
        )}
        <div className="space-y-4">
          {visibleMessages.map(message => (
            <div
              key={message.id}
              ref={message.id === anchoredMessageId ? anchoredMessageRef : undefined}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex max-w-[88%] flex-col ${message.sender === 'user' ? 'items-end' : 'items-start'}`}>
                {message.kind === 'secret_request' ? (
                  <SecretCard conversationId={conversation.id} message={message} onResolved={() => load(true)} />
                ) : (
                  <div className={`chat-message-bubble rounded-2xl px-3.5 py-2.5 text-sm ${
                    message.sender === 'user'
                      ? 'rounded-br-sm bg-[#177052] text-white'
                      : message.kind === 'runtime_followup'
                        ? 'rounded-bl-sm border border-amber-200 bg-amber-50 text-gray-800'
                        : 'rounded-bl-sm bg-gray-100 text-gray-800'
                  }`}>
                    {message.kind === 'runtime_followup' && (
                      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                        {message.resolvedAt ? 'Resolved runtime question' : 'Your help is needed'}
                      </div>
                    )}
                    <MessageBody text={message.message} renderMarkup={message.sender !== 'user'} />
                  </div>
                )}
                <MessageFooter message={message} />
              </div>
            </div>
          ))}
          {recipeGenerationActive && (
            <div className="flex justify-start">
              <div className="chat-message-bubble flex items-center gap-2 rounded-2xl rounded-bl-sm bg-gray-100 px-3.5 py-2.5 text-sm text-gray-700">
                <TypingDots />
                <span>Generating recipe…</span>
              </div>
            </div>
          )}
          {showStarters && (
            <div className="flex flex-wrap gap-2 pt-1">
              {STARTER_SUGGESTIONS.map(suggestion => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => void send(suggestion)}
                  className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 transition hover:border-amber-300 hover:bg-amber-100"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
          {streaming && (
            <>
              {streamingText && (
                <div className="flex justify-start">
                  <div className="chat-message-bubble max-w-[88%] rounded-2xl rounded-bl-sm bg-gray-100 px-3.5 py-2.5 text-sm text-gray-800">
                    <StreamingSaltShaker active={streaming} value={streamingText}>
                      <MessageBody text={streamingText} />
                    </StreamingSaltShaker>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <TypingDots />
                <span>{chefStatus || 'Chef is working…'}</span>
              </div>
            </>
          )}
          {chefTerminalProblem && (
            <div className={`rounded-lg p-3 text-xs ${chefJob.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
              <div>{chefJob.errorMessage || (chefJob.status === 'cancelled' ? 'Chef was stopped before completing the answer.' : 'Chef could not complete that request.')}</div>
              {chefJob.partialContent && (
                <details className="mt-2">
                  <summary className="cursor-pointer font-medium">Show preserved draft</summary>
                  <div className="mt-2 whitespace-pre-wrap">{chefJob.partialContent}</div>
                </details>
              )}
              <button type="button" onClick={() => void retryChef()} className="mt-2 font-semibold text-blue-700 hover:text-blue-900">
                Retry
              </button>
            </div>
          )}
          {planLimit && <PlanLimitCard limit={planLimit} />}
          {agentError && <div className="rounded-lg bg-red-50 p-2 text-xs text-red-700">{agentError}</div>}
          <div ref={anchoredTurnEndRef} aria-hidden="true" />
          {anchoredMessageId && anchoredTurnSpace > 0 && (
            <div style={{ height: anchoredTurnSpace }} aria-hidden="true" />
          )}
        </div>
      </div>

      <footer className={isYolo
        ? 'pointer-events-none absolute inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-10'
        : 'shrink-0 border-t border-gray-200 bg-gray-50 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'}>
        <div className={`flex items-start gap-2 ${isYolo ? 'pointer-events-auto rounded-2xl border border-[#eadfca] bg-white/95 p-2 shadow-[0_14px_36px_rgba(83,63,32,0.18)] backdrop-blur-sm' : ''}`}>
          <div className="min-w-0 flex-1">
            <textarea
              ref={inputRef}
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
              rows={2}
              placeholder="Ask Chef to review, explain, or change this recipe…"
              className={`min-h-[48px] w-full resize-none rounded-xl bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 ${isYolo ? 'border-0 shadow-none focus:ring-amber-200' : 'border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-100'}`}
            />
            <div className="px-3 pt-0.5 text-[11px] text-stone-400">Enter to send · Shift+Enter for a new line</div>
          </div>
          {chefActive ? (
            <button
              type="button"
              onClick={() => void stopChef()}
              disabled={chefJob?.cancelRequested}
              aria-label={chefJob?.cancelRequested ? 'Stopping generation' : 'Stop generation'}
              title={chefJob?.cancelRequested ? 'Stopping…' : 'Stop generation'}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-stone-700 text-white shadow-sm transition hover:bg-stone-800 disabled:cursor-wait disabled:opacity-50"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
            </button>
          ) : (
            <button onClick={() => void send()} disabled={!input.trim() || startingRecipe} className={`h-12 rounded-xl px-4 text-sm font-semibold text-white shadow-sm ${isYolo ? 'bg-amber-600 hover:bg-amber-700 disabled:bg-stone-300' : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300'}`}>
              {startingRecipe ? 'Sending…' : 'Send'}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default ChatBox;
